import { type ReturnData } from "src/types";
import { logError } from "src/services/logger";

type RecordingUploadQueueConfig = {
  uploadChunk: (blob: Blob, chunkId: number) => Promise<ReturnData>;
  uploadEnd: () => Promise<ReturnData>;
  onError?: (error: unknown) => void;
};

type RecordingUploadQueue = {
  enqueueChunk: (blob: Blob) => Promise<void>;
  finalize: () => Promise<ReturnData>;
  reset: () => void;
};

export const createRecordingUploadQueue = (config: RecordingUploadQueueConfig): RecordingUploadQueue => {
  let uploadChain: Promise<void> = Promise.resolve();
  let hasUploadedChunk = false;
  let isEnding = false;
  let chunkId = 1;
  let finalizePromise: Promise<ReturnData> | null = null;

  const reportError = (error: unknown) => {
    logError("RecordingUploadQueue: falha ao enviar chunk da gravação", error, { chunkId: chunkId - 1 });

    if (config?.onError) {
      config?.onError(error);
    }
  };

  const enqueueTask = (task: () => Promise<ReturnData>) => {
    uploadChain = uploadChain
      .then(task)
      .then(() => undefined)
      .catch((error) => {
        reportError(error);
      });

    return uploadChain;
  };

  const enqueueChunk = (blob: Blob) => {
    if (isEnding) return Promise.resolve();

    hasUploadedChunk = true;

    const currentChunkId = chunkId;
    chunkId++;

    return enqueueTask(() => config.uploadChunk(blob, currentChunkId));
  };

  const finalize = (): Promise<ReturnData> => {
    if (finalizePromise) return finalizePromise;

    finalizePromise = (async () => {
      isEnding = true;

      await uploadChain;

      if (!hasUploadedChunk) {
        return { success: true };
      }

      return config.uploadEnd();
    })();

    return finalizePromise;
  };

  const reset = () => {
    uploadChain = Promise.resolve();
    hasUploadedChunk = false;
    isEnding = false;
    chunkId = 1;
    finalizePromise = null;
  };

  return {
    enqueueChunk,
    finalize,
    reset,
  };
};
